import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/lovable-demo/components/bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { fmtCurrency } from "@/lovable-demo/lib/warranty/calc";
import { usePermissions } from "@/lovable-demo/hooks/use-permissions";
import {
  DECISION_OPTIONS,
  LIFECYCLE,
  STATUS_LABEL,
  TRIGGER_CATEGORIES,
  acknowledgeEscalation,
  advanceEscalationLifecycle,
  changeEscalationLevel,
  dueLabel,
  evaluateEscalationTriggers,
  fmtDateTime,
  isOpen,
  isOverdue,
  isPostDecision,
  isResponseOverdue,
  raiseHumanEscalation,
  recordManagementDecision,
  triggerLabel,
  useEscalations,
  type Escalation,
  type EscalationEvent,
  type EvidenceSnapshot,
} from "@/lovable-demo/lib/governance/escalations";

export function SeverityPill({ e }: { e: Escalation }) {
  const tone =
    e.severity === "critical"
      ? "border-risk-critical/40 bg-risk-critical/15 text-risk-critical"
      : "border-risk-high/40 bg-risk-high/15 text-risk-high";
  return (
    <span
      className={cn(
        "inline-flex rounded border px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider",
        tone,
      )}
    >
      {e.severity}
    </span>
  );
}

export function EscStatusPill({ e }: { e: Escalation }) {
  const overdue = isOverdue(e);
  const tone = overdue
    ? "border-destructive/40 bg-destructive/15 text-destructive"
    : e.status === "decided"
      ? "border-success/40 bg-success/12 text-success"
      : e.status === "open"
        ? "border-info/40 bg-info/12 text-info"
        : "border-warning/40 bg-warning/12 text-warning";
  return (
    <span
      className={cn(
        "inline-flex rounded border px-2 py-0.5 text-[0.62rem] font-medium uppercase tracking-wider",
        tone,
      )}
    >
      {overdue ? "Past due" : (STATUS_LABEL[e.status] ?? e.status)}
    </span>
  );
}

/** Compact row used by Executive Summary and Daily Briefing; links to the governed record. */
export function EscalationRow({ e, extra }: { e: Escalation; extra?: ReactNode }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/demo/problem-record/$id"
            params={{ id: e.record_id }}
            className="num text-xs font-semibold text-primary hover:underline"
          >
            {e.record_code}
          </Link>
          <LevelPill e={e} />
          <EscStatusPill e={e} />
          <SafetyPill e={e} />
        </div>
        <p className="mt-1 text-sm text-foreground">
          {e.manager_decision
            ? `${e.manager_decision}: ${e.decision_required}`
            : e.decision_required || e.reason}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {e.reason}
          {extra}
        </p>
      </div>
      <div className="text-right text-xs">
        <div
          className={cn(
            "num font-semibold",
            isOverdue(e) ? "text-destructive" : "text-foreground/80",
          )}
        >
          {e.closed_at
            ? `Closed ${fmtDateTime(e.closed_at)}`
            : e.decided_at
              ? `Decided ${fmtDateTime(e.decided_at)}`
              : e.level === 1
                ? "Monitoring"
                : dueLabel(e)}
        </div>
        <div className="text-muted-foreground">{e.escalated_to_label}</div>
      </div>
    </li>
  );
}

export function EscalationGroup({
  title,
  items,
  empty,
  extra,
}: {
  title: string;
  items: Escalation[];
  empty: string;
  extra?: (e: Escalation) => ReactNode;
}) {
  return (
    <div>
      <h3 className="flex items-center gap-2 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {title}{" "}
        <span className="num rounded bg-elevated px-1.5 text-foreground">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="py-2 text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border/60">
          {items.map((e) => (
            <EscalationRow key={e.id} e={e} extra={extra?.(e)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SnapshotView({ s, label }: { s: EvidenceSnapshot; label: string }) {
  if (!s || !s.capturedAt)
    return <p className="text-xs text-muted-foreground">{label}: not captured.</p>;
  return (
    <div className="rounded border border-border/70 bg-elevated/40 p-3">
      <p className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
        {label} · captured {fmtDateTime(s.capturedAt)}
      </p>
      <dl className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-muted-foreground">Risk</dt>
          <dd className="num font-semibold uppercase">
            {s.risk} {s.riskScore}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Exposure</dt>
          <dd className="num font-semibold">{fmtCurrency(Number(s.exposure ?? 0))}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Stages complete</dt>
          <dd className="num font-semibold">{s.stagesComplete}/11</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Evidence items</dt>
          <dd className="num font-semibold">
            {s.evidenceCount} · {s.violatedClauses} violated clauses
          </dd>
        </div>
      </dl>
      {s.evidence && s.evidence.length > 0 && (
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-primary">
            Evidence on file at this point ({s.evidence.length})
          </summary>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-foreground/80">
            {s.evidence.map((ev, i) => (
              <li key={i}>
                {ev.name} <span className="text-muted-foreground">— {ev.stage}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}

function DecisionForm({ e, onDone }: { e: Escalation; onDone: () => void }) {
  const [decision, setDecision] = useState<string>("");
  const [rationale, setRationale] = useState("");
  const [actions, setActions] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await recordManagementDecision({
        id: e.id,
        decision,
        rationale,
        actions: actions
          .split("\n")
          .map((a) => a.trim())
          .filter(Boolean),
      });
      toast.success("Management decision recorded with audit entry and evidence snapshot.");
      onDone();
    } catch (err) {
      toast.error(`Decision not recorded: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="no-print space-y-2 rounded border border-primary/30 bg-primary/5 p-3">
      <p className="text-xs font-semibold text-foreground">Record management decision</p>
      <div className="flex flex-wrap gap-1.5">
        {DECISION_OPTIONS.map((d) => (
          <Button
            key={d}
            type="button"
            size="sm"
            variant={decision === d ? "default" : "outline"}
            className="h-7 text-xs"
            onClick={() => setDecision(d)}
          >
            {d}
          </Button>
        ))}
      </div>
      <Textarea
        placeholder="Decision rationale (required)"
        value={rationale}
        onChange={(ev) => setRationale(ev.target.value)}
        className="min-h-16 text-sm"
      />
      <Textarea
        placeholder="Resulting actions — one per line (action — owner)"
        value={actions}
        onChange={(ev) => setActions(ev.target.value)}
        className="min-h-14 text-sm"
      />
      <p className="text-[0.68rem] text-muted-foreground">
        You choose the decision and rationale. Broadview then verifies your decision authority and
        records the decision, audit entry and evidence snapshot together before confirming.
      </p>
      <Button
        size="sm"
        disabled={busy || !decision || rationale.trim().length < 10}
        onClick={submit}
      >
        {busy ? "Recording…" : "Record decision"}
      </Button>
    </div>
  );
}

export function LevelPill({ e }: { e: Escalation }) {
  const tone =
    e.level >= 3
      ? "border-risk-critical/50 bg-risk-critical/15 text-risk-critical"
      : e.level === 2
        ? "border-warning/40 bg-warning/12 text-warning"
        : "border-info/40 bg-info/12 text-info";
  return (
    <span
      className={cn(
        "inline-flex rounded border px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider",
        tone,
      )}
    >
      L{e.level} ·{" "}
      {e.level >= 3 ? "Critical / Executive" : e.level === 2 ? "Management" : "Attention"}
    </span>
  );
}

export function SafetyPill({ e }: { e: Escalation }) {
  if (!e.safety_regulatory) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded border border-destructive/50 px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider text-destructive">
      <ShieldAlert className="size-3" /> Safety / regulatory override
    </span>
  );
}

function LifecycleStepper({ e }: { e: Escalation }) {
  const idx = LIFECYCLE.findIndex(([k]) => k === e.status);
  return (
    <ol className="mt-3 flex flex-wrap items-center gap-1 text-[0.62rem] uppercase tracking-wider">
      {LIFECYCLE.map(([k, label], i) => (
        <li
          key={k}
          className={cn(
            "rounded border px-1.5 py-0.5",
            i === idx
              ? "border-primary bg-primary/15 text-primary"
              : i < idx
                ? "border-success/40 text-success"
                : "border-border text-muted-foreground",
          )}
        >
          {label}
        </li>
      ))}
    </ol>
  );
}

function TriggerList({ e }: { e: Escalation }) {
  const list = [...e.contributing_triggers].sort(
    (a, b) => Number(!!b.primary) - Number(!!a.primary),
  );
  if (!list.length) return null;
  return (
    <div className="mt-3">
      <p className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
        Triggers ({list.length}) · one governed escalation ·{" "}
        {e.initiation_method === "human" ? "human-initiated" : "rule-triggered"}
      </p>
      <ul className="mt-1 divide-y divide-border/60 rounded border border-border/70 text-xs">
        {list.map((t, i) => (
          <li key={i} className="flex flex-wrap items-start justify-between gap-2 px-2 py-1.5">
            <span>
              <span className="font-semibold text-foreground">{t.category}</span>
              {t.primary && (
                <span className="ml-1 rounded bg-primary/15 px-1 text-[0.6rem] uppercase text-primary">
                  Primary
                </span>
              )}
              <span className="block text-muted-foreground">{t.rule}</span>
            </span>
            <span className="num text-right text-foreground/85">
              Observed{" "}
              {typeof t.observed === "number"
                ? t.observed.toLocaleString("en-US")
                : (t.observed ?? "—")}
              {t.threshold !== "" && t.threshold != null && (
                <span className="block text-muted-foreground">
                  Threshold {Number(t.threshold).toLocaleString("en-US")}
                </span>
              )}
              <span className="block text-muted-foreground">Level {t.level}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LevelChangeForm({
  e,
  isDirector,
  onDone,
}: {
  e: Escalation;
  isDirector: boolean;
  onDone: () => void;
}) {
  const [level, setLevel] = useState<number>(0);
  const [rationale, setRationale] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const down = level > 0 && level < e.level;
  const refs = evidence
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
  const submit = async () => {
    setBusy(true);
    try {
      await changeEscalationLevel(e.id, level, rationale, refs);
      toast.success(`Escalation level changed to ${level} and recorded in the audit trail.`);
      onDone();
    } catch (err) {
      toast.error(`Level not changed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="no-print space-y-2 rounded border border-border p-3">
      <p className="text-xs font-semibold text-foreground">Change escalation level</p>
      <div className="flex flex-wrap gap-1.5">
        {[1, 2, 3]
          .filter((l) => l !== e.level)
          .map((l) => (
            <Button
              key={l}
              size="sm"
              type="button"
              variant={level === l ? "default" : "outline"}
              className="h-7 text-xs"
              disabled={l < e.level && !isDirector}
              onClick={() => setLevel(l)}
            >
              {l > e.level ? "Escalate" : "De-escalate"} to Level {l}
            </Button>
          ))}
      </div>
      {e.safety_regulatory && down && (
        <p className="text-[0.68rem] text-destructive">
          Safety / regulatory condition: de-escalation is a Quality Director disposition with
          evidence — it is never driven by financial or scoring factors.
        </p>
      )}
      <Textarea
        placeholder="Rationale (required)"
        value={rationale}
        onChange={(ev) => setRationale(ev.target.value)}
        className="min-h-14 text-sm"
      />
      {down && (
        <Textarea
          placeholder="Supporting evidence references — one per line (required for de-escalation)"
          value={evidence}
          onChange={(ev) => setEvidence(ev.target.value)}
          className="min-h-12 text-sm"
        />
      )}
      <Button
        size="sm"
        disabled={busy || !level || rationale.trim().length < 10 || (down && refs.length === 0)}
        onClick={submit}
      >
        {busy ? "Recording…" : "Record level change"}
      </Button>
    </div>
  );
}

function LifecycleForm({
  e,
  isDirector,
  onDone,
}: {
  e: Escalation;
  isDirector: boolean;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [evidence, setEvidence] = useState("");
  const [effective, setEffective] = useState(false);
  const [residual, setResidual] = useState(false);
  const [busy, setBusy] = useState(false);
  const refs = evidence
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
  const go = async (
    to: "actions_in_progress" | "effectiveness_verification" | "closed",
    eff?: boolean,
  ) => {
    setBusy(true);
    try {
      await advanceEscalationLifecycle({
        id: e.id,
        to,
        note,
        evidence: refs,
        effective: eff,
        residualOk: to === "closed" ? residual : undefined,
      });
      toast.success("Lifecycle step recorded with audit entry.");
      setNote("");
      onDone();
    } catch (err) {
      toast.error(`Not recorded: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  const short = note.trim().length < 10;
  return (
    <div className="no-print space-y-2 rounded border border-border p-3">
      <p className="text-xs font-semibold text-foreground">
        {e.status === "decided"
          ? "Start resulting actions"
          : e.status === "actions_in_progress"
            ? "Complete resulting actions"
            : "Verify effectiveness & authorize closure"}
      </p>
      <Textarea
        placeholder="Note (required)"
        value={note}
        onChange={(ev) => setNote(ev.target.value)}
        className="min-h-12 text-sm"
      />
      {e.status === "decided" && (
        <Button size="sm" disabled={busy || short} onClick={() => go("actions_in_progress")}>
          Start actions
        </Button>
      )}
      {e.status === "actions_in_progress" && (
        <Button size="sm" disabled={busy || short} onClick={() => go("effectiveness_verification")}>
          Actions complete — begin effectiveness check
        </Button>
      )}
      {e.status === "effectiveness_verification" && (
        <>
          <Textarea
            placeholder="Effectiveness evidence references — one per line"
            value={evidence}
            onChange={(ev) => setEvidence(ev.target.value)}
            className="min-h-12 text-sm"
          />
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={effective}
              onChange={(ev) => setEffective(ev.target.checked)}
            />{" "}
            Effectiveness verified
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={residual}
              onChange={(ev) => setResidual(ev.target.checked)}
            />{" "}
            Residual risk acceptable
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy || short}
              onClick={() => go("actions_in_progress", false)}
            >
              Not effective — reopen actions
            </Button>
            <Button
              size="sm"
              disabled={
                busy || short || !isDirector || !effective || !residual || refs.length === 0
              }
              onClick={() => go("closed", true)}
            >
              Authorize closure
            </Button>
          </div>
          {!isDirector && (
            <p className="text-[0.68rem] text-muted-foreground">
              Authorized closure requires the Quality Director.
            </p>
          )}
        </>
      )}
      <p className="text-[0.68rem] text-muted-foreground">
        A decision does not close an escalation; closure needs completed actions, verified
        effectiveness and acceptable residual risk.
      </p>
    </div>
  );
}

function EscalationCard({
  e,
  events,
  canDecide,
  canRaise,
  isDirector,
  problemClock,
  onChange,
}: {
  e: Escalation;
  events: EscalationEvent[];
  canDecide: boolean;
  canRaise: boolean;
  isDirector: boolean;
  problemClock?: string | undefined;
  onChange: () => void;
}) {
  const [deciding, setDeciding] = useState(false);
  const [levelOpen, setLevelOpen] = useState(false);
  const ack = async () => {
    try {
      await acknowledgeEscalation(e.id, "");
      toast.success("Escalation acknowledged and recorded in the audit trail.");
      onChange();
    } catch (err) {
      toast.error(`Not acknowledged: ${(err as Error).message}`);
    }
  };
  const closed = e.status === "closed";
  return (
    <article
      className={cn(
        "rounded-lg border p-4",
        isOverdue(e) || isResponseOverdue(e) ? "border-destructive/50" : "border-border",
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <LevelPill e={e} />
        <SeverityPill e={e} />
        <EscStatusPill e={e} />
        <SafetyPill e={e} />
        <span className="text-xs text-muted-foreground">
          Primary trigger: {e.primary_trigger_category || triggerLabel(e.trigger_key)}
        </span>
      </header>
      <LifecycleStepper e={e} />
      {e.safety_regulatory && e.response_requirement === "immediate" && (
        <p
          className={cn(
            "mt-3 rounded border p-2 text-xs",
            isResponseOverdue(e)
              ? "border-destructive/60 bg-destructive/10 text-destructive"
              : "border-destructive/40 bg-destructive/5 text-foreground/90",
          )}
        >
          <span className="font-semibold">Immediate governed response required</span> by{" "}
          {fmtDateTime(e.response_due_at)}
          {e.acknowledged_at
            ? ` · responded ${fmtDateTime(e.acknowledged_at)}`
            : isResponseOverdue(e)
              ? " · response overdue"
              : ""}
          . Safety / regulatory conditions are independent overrides and are not reduced by
          financial exposure or priority scoring.
        </p>
      )}
      {e.level === 1 ? (
        <div className="mt-3 rounded border border-info/30 bg-info/5 p-3 text-sm">
          <p className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
            Attention — no formal management decision yet
          </p>
          <p className="mt-1 text-foreground">{e.reason}</p>
        </div>
      ) : (
        <div className="mt-3 rounded border border-border/70 bg-elevated/30 p-3">
          <p className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
            Decision required
          </p>
          <p className="mt-1 text-[0.95rem] font-semibold leading-snug text-foreground">
            {e.decision_required}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Why escalated: {e.reason}</p>
        </div>
      )}
      <dl className="mt-3 grid gap-3 sm:grid-cols-4">
        <Fact label="Escalated">{fmtDateTime(e.created_at)}</Fact>
        <Fact label="Escalated by">{e.raised_by_label || "—"}</Fact>
        <Fact label="Escalated to / authority">{e.escalated_to_label}</Fact>
        <Fact label="Required participants">{e.required_participants.join(", ") || "—"}</Fact>
        <Fact label="Decision clock (management)">
          <span className={isOverdue(e) ? "text-destructive" : undefined}>
            {e.level === 1
              ? "Not started (Attention)"
              : `${fmtDateTime(e.due_at)} · ${dueLabel(e)}`}
          </span>
        </Fact>
        <Fact label="Problem-management clock">{problemClock ?? "See workflow stages"}</Fact>
        <Fact label="Exposure at escalation">
          {e.financial_exposure_at_escalation != null
            ? fmtCurrency(Number(e.financial_exposure_at_escalation))
            : "—"}
        </Fact>
        <Fact label="Safety / regulatory">
          {e.safety_regulatory ? "Yes — override applies" : "No"}
        </Fact>
      </dl>
      <TriggerList e={e} />
      {e.ai_recommendation && (
        <p className="mt-3 rounded border border-info/30 bg-info/5 p-2 text-xs text-foreground/85">
          <span className="font-semibold text-info">AI advisory (not a decision). </span>
          {e.ai_recommendation.replace(/^Advisory:\s*/, "")}
        </p>
      )}
      {e.manager_decision && (
        <div className="mt-3 space-y-2 rounded border border-success/30 bg-success/5 p-3">
          <dl className="grid gap-3 sm:grid-cols-3">
            <Fact label="Manager decision">
              <span className="font-semibold">{e.manager_decision}</span>
            </Fact>
            <Fact label="Decided by">{e.decided_by_label}</Fact>
            <Fact label="Decided at">{fmtDateTime(e.decided_at)}</Fact>
          </dl>
          <Fact label="Decision rationale">{e.decision_rationale}</Fact>
          <Fact label="Resulting actions → effectiveness">
            {e.resulting_actions.length ? (
              <ul className="list-disc pl-5">
                {e.resulting_actions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            ) : (
              "None recorded"
            )}
            <span className="mt-1 block text-xs text-muted-foreground">
              Actions{" "}
              {e.actions_completed_at
                ? `completed ${fmtDateTime(e.actions_completed_at)}`
                : e.actions_started_at
                  ? `started ${fmtDateTime(e.actions_started_at)}`
                  : "not started"}{" "}
              · Effectiveness {e.effectiveness_result || "not yet verified"}
              {closed &&
                ` · closed by ${e.closed_by_label} ${fmtDateTime(e.closed_at)} (residual risk acceptable)`}
            </span>
          </Fact>
        </div>
      )}
      {!closed && (
        <div className="mt-3 flex flex-wrap gap-2">
          {isOpen(e) && canDecide && (
            <>
              {e.status === "open" && (
                <Button size="sm" variant="outline" onClick={ack}>
                  Acknowledge
                </Button>
              )}
              <Button
                size="sm"
                variant={deciding ? "secondary" : "default"}
                onClick={() => setDeciding(!deciding)}
              >
                {deciding ? "Cancel" : "Make decision"}
              </Button>
            </>
          )}
          {isOpen(e) && !canDecide && (
            <p className="text-xs text-muted-foreground">
              Awaiting the decision authority ({e.escalated_to_label}).
            </p>
          )}
          {canRaise && (
            <Button size="sm" variant="outline" onClick={() => setLevelOpen(!levelOpen)}>
              {levelOpen ? "Cancel" : "Change level"}
            </Button>
          )}
        </div>
      )}
      {deciding && isOpen(e) && (
        <div className="mt-3">
          <DecisionForm
            e={e}
            onDone={() => {
              setDeciding(false);
              onChange();
            }}
          />
        </div>
      )}
      {levelOpen && !closed && (
        <div className="mt-3">
          <LevelChangeForm
            e={e}
            isDirector={isDirector}
            onDone={() => {
              setLevelOpen(false);
              onChange();
            }}
          />
        </div>
      )}
      {isPostDecision(e) && canRaise && (
        <div className="mt-3">
          <LifecycleForm e={e} isDirector={isDirector} onDone={onChange} />
        </div>
      )}
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-semibold text-primary">
          Evidence &amp; audit history ({events.length})
        </summary>
        <div className="mt-2 space-y-2">
          <SnapshotView s={e.evidence_snapshot} label="Evidence at escalation" />
          {e.manager_decision && (
            <SnapshotView s={e.decision_evidence_snapshot} label="Evidence snapshot at decision" />
          )}
          <ol className="space-y-1.5 border-l border-border pl-3">
            {events.map((ev) => (
              <li key={ev.id} className="text-xs">
                <span className="num text-muted-foreground">{fmtDateTime(ev.occurred_at)}</span>{" "}
                <span className="font-semibold capitalize">{ev.event_type.replace(/_/g, " ")}</span>{" "}
                <span className="text-muted-foreground">by {ev.actor_label || "—"}</span>
                {ev.detail && <span className="block text-foreground/80">{ev.detail}</span>}
              </li>
            ))}
          </ol>
          <p className="text-[0.68rem] text-muted-foreground">
            History is append-only; entries cannot be edited or removed.
          </p>
        </div>
      </details>
    </article>
  );
}

function RaiseEscalationForm({ recordId, onDone }: { recordId: string; onDone: () => void }) {
  const [level, setLevel] = useState(2);
  const [category, setCategory] = useState<string>("Human Initiated");
  const [rationale, setRationale] = useState("");
  const [decision, setDecision] = useState("");
  const [safety, setSafety] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await raiseHumanEscalation({
        recordId,
        level,
        category,
        rationale,
        decisionRequired: decision,
        safety,
      });
      toast.success("Escalation recorded on this Problem Record with audit entry.");
      onDone();
    } catch (err) {
      toast.error(`Not raised: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="no-print space-y-2 rounded border border-primary/30 bg-primary/5 p-3">
      <p className="text-xs font-semibold text-foreground">Raise a human-initiated escalation</p>
      <div className="flex flex-wrap gap-2 text-xs">
        <select
          value={level}
          onChange={(ev) => setLevel(Number(ev.target.value))}
          className="rounded border border-border bg-background px-2 py-1"
        >
          <option value={1}>Level 1 · Attention</option>
          <option value={2}>Level 2 · Management Escalation</option>
          <option value={3}>Level 3 · Critical / Executive</option>
        </select>
        <select
          value={category}
          onChange={(ev) => setCategory(ev.target.value)}
          className="rounded border border-border bg-background px-2 py-1"
        >
          {TRIGGER_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={safety} onChange={(ev) => setSafety(ev.target.checked)} />{" "}
          Safety / regulatory condition (forces Level 3)
        </label>
      </div>
      <Textarea
        placeholder="Rationale — what condition you observed (required)"
        value={rationale}
        onChange={(ev) => setRationale(ev.target.value)}
        className="min-h-14 text-sm"
      />
      {level >= 2 && (
        <Textarea
          placeholder="Decision required from management"
          value={decision}
          onChange={(ev) => setDecision(ev.target.value)}
          className="min-h-12 text-sm"
        />
      )}
      <p className="text-[0.68rem] text-muted-foreground">
        If this record already has an active escalation, your condition is added to it as a
        contributing trigger — never a duplicate.
      </p>
      <Button size="sm" disabled={busy || rationale.trim().length < 10} onClick={submit}>
        {busy ? "Recording…" : "Raise escalation"}
      </Button>
    </div>
  );
}

/** Problem Record section: the governed record is the system of record for its escalations. */
export function RecordEscalations({
  recordId,
  recordCode,
  problemClock,
}: {
  recordId: string;
  recordCode: string;
  problemClock?: string | undefined;
}) {
  const { escalations, events, loading, refresh } = useEscalations(recordId);
  const { roles } = usePermissions();
  const isDirector = roles.includes("quality_director") || roles.includes("admin");
  const canDecide = isDirector;
  const canRaise = roles.some((r) =>
    [
      "admin",
      "quality_director",
      "quality_engineer",
      "manufacturing_engineer",
      "supplier_quality",
    ].includes(r),
  );
  const [raising, setRaising] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const active = escalations.filter((e) => e.status !== "closed");
  const closed = escalations.filter((e) => e.status === "closed");
  const evaluate = async () => {
    setEvaluating(true);
    try {
      const r = await evaluateEscalationTriggers(recordId);
      toast.success(
        r.met.length
          ? `${r.met.length} approved condition(s) met — recorded on one governed escalation (Level ${r.level}).`
          : "No approved escalation condition is met.",
      );
      void refresh();
    } catch (err) {
      toast.error(`Evaluation not recorded: ${(err as Error).message}`);
    } finally {
      setEvaluating(false);
    }
  };
  return (
    <Panel
      title="Escalation & Management Decision Intelligence"
      subtitle={`Governed escalation record for ${recordCode}. Executive Dashboard, Executive Summary, Daily Briefing and Financial Intelligence show these same entries.`}
    >
      {canRaise && (
        <div className="no-print mb-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={evaluating} onClick={evaluate}>
            {evaluating ? "Evaluating…" : "Evaluate escalation triggers"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setRaising(!raising)}>
            {raising ? "Cancel" : "Raise escalation"}
          </Button>
        </div>
      )}
      {raising && (
        <div className="mb-3">
          <RaiseEscalationForm
            recordId={recordId}
            onDone={() => {
              setRaising(false);
              void refresh();
            }}
          />
        </div>
      )}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading escalations…</p>
      ) : escalations.length === 0 ? (
        <p className="text-sm text-muted-foreground">No escalation is active on this record.</p>
      ) : (
        <div className="space-y-3">
          {active.map((e) => (
            <EscalationCard
              key={e.id}
              e={e}
              canDecide={canDecide}
              canRaise={canRaise}
              isDirector={isDirector}
              problemClock={problemClock}
              events={events.filter((x) => x.escalation_id === e.id)}
              onChange={refresh}
            />
          ))}
          {closed.length > 0 && (
            <details open={active.length === 0}>
              <summary className="cursor-pointer text-xs font-semibold text-muted-foreground">
                Closed escalations ({closed.length})
              </summary>
              <div className="mt-2 space-y-3">
                {closed.map((e) => (
                  <EscalationCard
                    key={e.id}
                    e={e}
                    canDecide={false}
                    canRaise={false}
                    isDirector={false}
                    events={events.filter((x) => x.escalation_id === e.id)}
                    onChange={refresh}
                  />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
      <p className="mt-3 text-[0.68rem] text-muted-foreground">
        Broadview determines when an approved condition is met and packages the evidence; the
        accountable person makes the decision. AI may explain, never decide.
      </p>
    </Panel>
  );
}
